import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "flex items-center space-x-4" }
const _hoisted_4 = { class: "space-y-2" }
const _hoisted_5 = { class: "flex items-center space-x-4" }
const _hoisted_6 = { class: "min-w-full divide-y divide-gray-300" }
const _hoisted_7 = { class: "bg-gray-200" }
const _hoisted_8 = { class: "divide-y divide-gray-200" }

import { ref, Ref, computed, watch, ComputedRef, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import { IntegrationService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

import {
    IntegrationLogListFilters,
    IntegrationLogListItem,
    IntegrationLogListSort,
} from '@/views/pages/integration/components';

import {
    FilterItem,
    IntegrationLogModel,
    IntegrationServiceGetLogsParams,
    LevelModel,
    IntegrationLogActions,
    IntegrationLogGetters,
    SortMenuItem,
} from '@/types';

import { LinkIcon } from '@heroicons/vue/solid';

const perPage = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'IntegrationLogPage',
  props: {
    type: {
        type: String,
    },
},
  setup(__props) {

const route = useRoute();
const router = useRouter();

const props = __props;

const filterStore = useFilterStore();
const uiStore = useUiStore();

const integrationId = Number(route.params.id);
const cols = [
    'callId',
    'callCreatedAt',
    'callType',
    'callStatus',
    'callSource',
    'callDestination',
    'createdAt',
    'status',
    'externalId',
    'observation',
];

const integrationLogs: Ref<IntegrationLogModel[]> = ref([]);
const count = ref(0);

const search = computed<string>({
    get: () => store.getters[IntegrationLogGetters.SEARCH],
    set: (search: string) => store.dispatch(IntegrationLogActions.SET_SEARCH, search),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[IntegrationLogGetters.SORT],
    set: (sort: SortMenuItem[]) => store.dispatch(IntegrationLogActions.SET_SORT, sort),
});

const currentPage = computed<number>({
    get: () => store.getters[IntegrationLogGetters.PAGE],
    set: (page: number) => store.dispatch(IntegrationLogActions.SET_PAGE, page),
});

const orderBy = computed<string>(() =>
    sortMenuSelected.value.map((s) => s.direction + s.key).join(','),
);

const filter = computed(() => store.getters[IntegrationLogGetters.FILTER]);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[IntegrationLogGetters.ACTIVE_FILTERS],
);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getIntegrationLogs = async () => {
    const params: IntegrationServiceGetLogsParams = {
        page: currentPage.value,
        per_page: perPage,
        order_by: orderBy.value,
        search: search.value,
        status: filter.value.status?.value,
    };

    try {
        await setLoading(true);
        const response = await IntegrationService.getLogs<Array<IntegrationLogModel>>(
            integrationId,
            { params },
        );

        integrationLogs.value = response.data;
        count.value = parseInt(response.headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const handleRemoveSortClick = (item: SortMenuItem) => {
    sortMenuSelected.value.splice(sortMenuSelected.value.indexOf(item), 1);
};

const handleRemoveFilterClick = (item: FilterItem) => {
    store.dispatch(IntegrationLogActions.CLEAR_FILTER, item);
};

watch(
    [levelFilter, search, filter, orderBy],
    () => {
        if (currentPage.value === 1) getIntegrationLogs();
        else currentPage.value = 1;
    },
    { deep: true },
);

watch([currentPage], () => {
    getIntegrationLogs();
});

search.value = '';
sortMenuSelected.value = [];
currentPage.value = 1;
router.replace({ query: {} });

onMounted(getIntegrationLogs);

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiSortMenuBadge = _resolveComponent("UiSortMenuBadge")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(_unref(LinkIcon), {
            class: "mr-1 h-6 w-6 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('core.entity.Integrations')) + " - " + _toDisplayString(_ctx.$t('integration.integrations.' + props.type + '.title')) + " - " + _toDisplayString(_ctx.$t('integration.labels.logs')), 1)
        ])
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListIntegration' } })
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(_component_UiPanel, {
        class: "flex px-4 py-2",
        "no-padding": ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_UiListSearch, {
            modelValue: search.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
            placeholder: _ctx.$t('core.actions.Search'),
            class: "flex-1"
          }, null, 8, ["modelValue", "placeholder"]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(IntegrationLogListSort), {
              modelValue: sortMenuSelected.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sortMenuSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_unref(IntegrationLogListFilters))
          ])
        ]),
        _: 1
      }),
      (sortMenuSelected.value.length || activeFilters.value.length)
        ? (_openBlock(), _createBlock(_component_UiPanel, {
            key: 0,
            class: "space-x-2"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortMenuSelected.value, (selected) => {
                return (_openBlock(), _createBlock(_component_UiSortMenuBadge, {
                  key: selected.key,
                  item: selected,
                  onRemove: ($event: any) => (handleRemoveSortClick(selected))
                }, null, 8, ["item", "onRemove"]))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (item) => {
                return (_openBlock(), _createBlock(_component_UiBadge, {
                  key: `activeFilter${item.field}`,
                  removable: "",
                  onRemove: ($event: any) => (handleRemoveFilterClick(item))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t(`integration.logs.filters.${item.field}.label`)) + ": " + _toDisplayString(item.label), 1)
                  ]),
                  _: 2
                }, 1032, ["onRemove"]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (integrationLogs.value.length)
        ? (_openBlock(), _createBlock(_component_UiPanel, {
            key: 1,
            "no-padding": "",
            rounded: false
          }, {
            default: _withCtx(() => [
              _createElementVNode("table", _hoisted_6, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", _hoisted_7, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(cols, (col) => {
                      return _createElementVNode("th", {
                        key: col,
                        scope: "col",
                        class: "whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-800"
                      }, _toDisplayString(_ctx.$t(`integration.logs.cols.${col}`)), 1)
                    }), 64))
                  ])
                ]),
                _createElementVNode("tbody", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(integrationLogs.value, (log) => {
                    return (_openBlock(), _createBlock(_unref(IntegrationLogListItem), {
                      key: log.id,
                      "model-value": log,
                      class: "even:bg-gray-100"
                    }, null, 8, ["model-value"]))
                  }), 128))
                ])
              ])
            ]),
            _: 1
          }))
        : (_openBlock(), _createBlock(_component_UiPanel, {
            key: 2,
            class: "flex items-center justify-center"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1)
            ]),
            _: 1
          })),
      (integrationLogs.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 3,
            modelValue: currentPage.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((currentPage).value = $event)),
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})